import classNames from 'classnames';
import * as React from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { subject } from '@casl/ability';

import { Agency, AgencyRepUserRoleEnum, ReportType, SalesRepUserRoleEnum, UserRole } from '__generated-api__';
import api from 'api';
import { useAbility, useAuthenticatedUser } from 'auth';
import Icon from 'components/icon';
import Form from 'components/Form/Form';
import { AutoSubmit } from 'components/Form/AutoSubmit';
import SelectField from 'components/Form/SelectField';
import { setLocationSearchParams } from 'utils/router';
import { MainHero } from 'my-account/components/MainHero';
import { DataTableHero } from 'my-account/components/DataTable/Hero';
import { DataTableClearFilterForm, DataTableListing } from 'my-account/components/DataTable/Listing';
import { caProvinces, usStates } from 'my-account/utils/states';
import { SelectUser } from 'my-account/components/SelectUser';
import { SelectTerritoryFilter } from 'my-account/components/SelectTerritoryFilter';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { getObjectKeys } from 'my-account/utils/object';
import { getAgencySalesGoalReport } from 'my-account/utils/agency';
import { DisplayPrice } from 'my-account/utils/currency';

const agenciesFiltersSchema = z.object({
  state: z.nativeEnum(usStates).or(z.nativeEnum(caProvinces)).or(z.literal('')),
  status: z
    .union([z.literal('1'), z.literal('0')])
    .optional()
    .default('1'),
  rep: z
    .object({
      id: z.number().positive(),
      first_name: z.string(),
      last_name: z.string(),
      email: z.string(),
    })
    .nullable()
    .optional(),
  territory_id: z.number().positive().nullable().optional(),
});

const AgenciesFilters: React.VFC = () => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const ability = useAbility();
  const canListRepresentatives = ability.can('list', subject('User', { role: SalesRepUserRoleEnum.SalesRep }));

  const initialValueParse = agenciesFiltersSchema.safeParse(
    Object.assign(
      {
        state: searchParams.get('state') ?? '',
        status: searchParams.get('status') ?? undefined,
      },
      searchParams.has('rep') && canListRepresentatives
        ? { rep: { id: Number(searchParams.get('rep')!), first_name: 'Loading...', last_name: '', email: '' } }
        : undefined,
      searchParams.has('territory') && canListRepresentatives
        ? { territory_id: Number(searchParams.get('territory')!) }
        : undefined
    )
  );

  return (
    <>
      <Form
        schema={agenciesFiltersSchema}
        initialValues={initialValueParse.success ? initialValueParse.data : {}}
        onSubmit={async (values) => {
          history.replace(
            setLocationSearchParams(location, {
              state: values.state ? values.state : undefined,
              status: values.status === '0' ? '0' : undefined,
              rep: values.rep ? String(values.rep.id) : undefined,
              territory: values.territory_id ? String(values.territory_id) : undefined,
              page: undefined,
              'list-page': undefined,
            })
          );
        }}
        isFiltersForm
      >
        <DataTableClearFilterForm />
        <AutoSubmit />
        <SelectField
          name="state"
          label="State/Province"
          allowEmpty
          emptyOptionLabel="Any State/Province"
          elStyle="fill"
          small
        >
          <optgroup label="United States">
            {getObjectKeys(usStates).map((state) => (
              <option key={usStates[state]} value={usStates[state]}>
                {state}
              </option>
            ))}
          </optgroup>
          <optgroup label="Canada">
            {getObjectKeys(caProvinces).map((state) => (
              <option key={caProvinces[state]} value={caProvinces[state]}>
                {state}
              </option>
            ))}
          </optgroup>
        </SelectField>
        <SelectField name="status" label="Status" elStyle="fill" small>
          <option value="1">Active</option>
          <option value="0">Archived</option>
        </SelectField>
        {canListRepresentatives && (
          <SelectUser
            name="rep"
            label="Representative"
            elStyle="fill"
            small
            queryParams={{ role: UserRole.SalesRep }}
            selectProps={{ isClearable: true }}
          />
        )}
        {canListRepresentatives && (
          <SelectTerritoryFilter
            name="territory_id"
            label="Territory"
            elStyle="fill"
            small
            selectProps={{ isClearable: true }}
          />
        )}
      </Form>
    </>
  );
};

export const AgencyRow: React.VFC<{ agency: Agency }> = ({ agency }) => {
  const user = useAuthenticatedUser();
  const history = useHistory();

  const detailsUrl = `/agencies/${agency.id}`;
  const salesGoalReportUrl = `/agencies/${agency.id}/sales-goal-report`;
  const salesGoalReport = getAgencySalesGoalReport(agency.goals);

  const columns = [];

  columns.push(
    <div key="agency-name" className="c-data-card__column c-data-card__column--first">
      {agency.territory?.region?.region_name && (
        <p className="c-data-card__subtitle">{agency.territory.region.region_name}</p>
      )}
      <p className="c-data-card__title">{agency.name}</p>
      <p className="c-data-card__label">Agency Name</p>
    </div>
  );

  if (salesGoalReport) {
    columns.push(
      <div key="agency-total-sales" className="c-data-card__column">
        <p>
          <DisplayPrice value={salesGoalReport.totalRevenue} />
        </p>
        <p className="c-data-card__label">Total Sales</p>
      </div>
    );

    columns.push(
      <div key="agency-goal-achieved" className="c-data-card__column">
        <p
          className={classNames({
            'u-text-success': Number.isFinite(salesGoalReport.percentGoalAchieved),
          })}
        >
          {Number.isFinite(salesGoalReport.percentGoalAchieved)
            ? `${(salesGoalReport.percentGoalAchieved * 100).toFixed(2)}%`
            : 'N/A'}
        </p>
        <p className="c-data-card__label">Goal Achieved</p>
      </div>
    );
  }

  if (user.role === AgencyRepUserRoleEnum.AgencyRep) {
    if (salesGoalReport) {
      columns.push(
        <div key="agency-link" className="c-data-card__column c-data-card__column--last">
          <button className="c-link-cta-basic c-link-cta--small" type="button">
            <span>View Report</span>
            <Icon name="arrow" className="o-svg-icon o-svg-right" />
          </button>
        </div>
      );
    }
  } else {
    columns.push(
      <div key="agency-link" className="c-data-card__column c-data-card__column--last">
        <button className="c-link-cta-basic c-link-cta--small" type="button">
          <span>Agency Details</span>
          <Icon name="arrow" className="o-svg-icon o-svg-right" />
        </button>
      </div>
    );

    if (salesGoalReport) {
      columns.push(
        <div key="agency-toolbar" className="c-data-card-toolbar">
          <button
            onClick={(event) => {
              event.preventDefault();
              history.push(salesGoalReportUrl);
            }}
            type="button"
          >
            <Icon name="invoice" className="c-data-card-toolbar__icon" />
            <span className="c-data-card-toolbar__label">Agency Report</span>
          </button>
        </div>
      );
    }
  }

  if (user.role === AgencyRepUserRoleEnum.AgencyRep) {
    if (!salesGoalReport) {
      return <div className="c-data-card c-data-card--agencies">{columns}</div>;
    }

    return (
      <Link to={salesGoalReportUrl} className="c-data-card c-data-card--agencies">
        {columns}
      </Link>
    );
  }

  return (
    <Link to={detailsUrl} className="c-data-card c-data-card--agencies">
      {columns}
    </Link>
  );
};

export default function AgenciesPage() {
  const user = useAuthenticatedUser();
  useDocumentTitle(user.role === AgencyRepUserRoleEnum.AgencyRep ? 'Agency Reports' : 'Agencies');
  const ability = useAbility();
  const canCreateAgency = ability.can('create', 'Agency');
  const canListRepresentatives = ability.can('list', subject('User', { role: SalesRepUserRoleEnum.SalesRep }));

  return (
    <>
      <MainHero />

      <DataTableHero
        title={user.role === AgencyRepUserRoleEnum.AgencyRep ? 'Agency Reports' : 'Agencies'}
        buttons={
          canCreateAgency ? (
            <>
              <Link to="/agencies/add" className="c-button c-button--primary c-button--small u-my-spacer-base-small">
                <Icon name="add" className="o-svg-icon o-svg-right" />
                <span>Add new agency</span>
              </Link>
            </>
          ) : undefined
        }
      />

      <section className="c-block c-block--spacing-b-extra-small c-block--spacing-b@md">
        <div className="o-container-fluid">
          <DataTableListing
            label="agencies"
            availableSortOptions={{
              created_at: 'Latest',
              name: 'Alphabetical',
              updated_at: 'Recently Updated',
            }}
            defaultSort="name"
            defaultDirection="asc"
            queryFn={api.agency.listAgencies}
            queryFnParams={(filters, searchParams) => {
              let state: string | undefined = searchParams.get('state') ?? '';
              if (!state.trim()) {
                state = undefined;
              }

              const status: 1 | 0 | undefined =
                searchParams.get('status') === '1' ? 1 : searchParams.get('status') === '0' ? 0 : undefined;

              let salesRepId: number | undefined =
                searchParams.has('rep') && canListRepresentatives ? Number(searchParams.get('rep')!) : undefined;
              if (!salesRepId || Number.isNaN(salesRepId) || salesRepId < 1) {
                salesRepId = undefined;
              }

              let territoryId: number | undefined =
                searchParams.has('territory') && canListRepresentatives
                  ? Number(searchParams.get('territory')!)
                  : undefined;
              if (!territoryId || Number.isNaN(territoryId) || territoryId < 1) {
                territoryId = undefined;
              }

              return { ...filters, state, status, salesRepId, territoryId };
            }}
            filters={() => <AgenciesFilters />}
            reportType={ReportType.Agency}
          >
            {(data) => (
              <>
                {data.map((agency) => (
                  <AgencyRow key={agency.id} agency={agency} />
                ))}
              </>
            )}
          </DataTableListing>
        </div>
      </section>
    </>
  );
}
